import React, { useState } from "react";
import { Alert, Button, MenuItem, Stack, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import { EXTERNAL_CHARGE_TYPES, chargeTypeLabelId } from "constants/external-charge-types";
import { formatFinancialAmount } from "../financial/financial-helpers";
import {
  DESCRIPTION_MAX_LENGTH,
  amountErrorId,
  descriptionErrorId,
  descriptionHelperText,
  isValidAmount,
  moneyFieldError,
  moneyRequestErrorMessage,
} from "../financial/money-validation";

const PlayerDebtWizard = ({ currency, excludedTypes = [], onCreate, onSuccess, onBack }) => {
  const intl = useIntl();
  const [step, setStep] = useState("form");
  const [type, setType] = useState("custom-fee");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const createDebt = async () => {
    setSaving(true);
    setError(null);
    try {
      await onCreate({ type, amount: Number(amount), description: description.trim() });
      setStep("result");
      onSuccess?.();
    } catch (requestError) {
      setError(moneyRequestErrorMessage(requestError, intl));
    } finally {
      setSaving(false);
    }
  };

  const amountError = moneyFieldError(intl, amountErrorId(amount));
  const canContinue = isValidAmount(amount) && description.trim() && !descriptionErrorId(description);

  if (step === "result") {
    return (
      <Stack spacing={2}>
        <RevaText variant="h6">{intl.formatMessage({ id: "gestion.playerFinance.debtCreated" })}</RevaText>
        <RevaText>{description}</RevaText>
        <RevaText sx={{ fontWeight: 600 }}>{formatFinancialAmount(amount, currency)}</RevaText>
        <Button variant="contained" onClick={onBack}>
          {intl.formatMessage({ id: "gestion.playerFinance.returnAccount" })}
        </Button>
      </Stack>
    );
  }

  if (step === "confirm") {
    return (
      <Stack spacing={2}>
        <RevaText variant="h6">{intl.formatMessage({ id: "gestion.playerFinance.confirmDebt" })}</RevaText>
        <RevaText>{intl.formatMessage({ id: chargeTypeLabelId(type) })}</RevaText>
        <RevaText>{description}</RevaText>
        <RevaText sx={{ fontWeight: 600 }}>{formatFinancialAmount(amount, currency)}</RevaText>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={saving} onClick={createDebt}>
            {intl.formatMessage({ id: "gestion.playerFinance.createDebt" })}
          </Button>
          <Button
            disabled={saving}
            onClick={() => {
              setError(null);
              setStep("form");
            }}
          >
            {intl.formatMessage({ id: "gestion.playerFinance.back" })}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <RevaText variant="h6">{intl.formatMessage({ id: "gestion.playerFinance.newDebt" })}</RevaText>
      <TextField
        select
        label={intl.formatMessage({ id: "gestion.playerFinance.type" })}
        value={type}
        onChange={(event) => setType(event.target.value)}
      >
        {EXTERNAL_CHARGE_TYPES.filter((chargeType) => !excludedTypes.includes(chargeType.value)).map((chargeType) => (
          <MenuItem key={chargeType.value} value={chargeType.value}>
            {intl.formatMessage({ id: chargeType.labelId })}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        type="number"
        label={`${intl.formatMessage({ id: "gestion.playerFinance.amount" })}${currency ? ` (${currency})` : ""}`}
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        error={Boolean(amountError)}
        helperText={amountError ?? undefined}
        inputProps={{ min: 0.01, step: 0.01, "data-testid": "debt-amount" }}
      />
      <TextField
        required
        label={intl.formatMessage({ id: "gestion.playerFinance.description" })}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        error={Boolean(descriptionErrorId(description))}
        helperText={descriptionHelperText(intl, description)}
        inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH, "data-testid": "debt-description" }}
      />
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={!canContinue}
          onClick={() => setStep("confirm")}
        >
          {intl.formatMessage({ id: "gestion.playerFinance.continue" })}
        </Button>
        <Button onClick={onBack}>{intl.formatMessage({ id: "gestion.playerFinance.back" })}</Button>
      </Stack>
    </Stack>
  );
};

export default PlayerDebtWizard;
